/**
 * Developers page. In-browser overview of the public HTTP/wire surface for
 * anyone building wallets, explorers, bots, or alt-clients. Mirrors and links
 * out to the full spec at docs/developers.md on GitHub.
 *
 * Source-of-truth references (kept here so the page is easy to update when
 * constants move): see server/api.ts, src/chain/transaction.ts,
 * src/chain/block.ts, src/chain/genesis.ts, src/crypto/pow.ts.
 */
export function mountDevelopers(host: HTMLElement): () => void {
  const view = document.createElement('div');
  view.className = 'view view-about';
  view.innerHTML = `
    <div class="view-header">
      <h2 class="view-title">For developers</h2>
    </div>

    <article class="about-body">
      <p class="lead">
        BrowserCoin's helper servers expose a fully open HTTP API: no auth,
         CORS <code>*</code>. Anyone can build a wallet, block
        explorer, mining bot, or alternative client against the chain. The
        wire format is documented end-to-end so you don't need to read the
        TypeScript source to integrate.
      </p>

      <p>
        Full spec with curl examples and field-level tables:
        <a href="https://github.com/swompythesecond/BrowserCoin/blob/main/docs/developers.md" target="_blank" rel="noopener noreferrer">docs/developers.md</a>.
        Runnable Node example that signs and submits a transaction:
        <a href="https://github.com/swompythesecond/BrowserCoin/blob/main/examples/send-tx.mjs" target="_blank" rel="noopener noreferrer">examples/send-tx.mjs</a>.
      </p>

      <h3>Network constants</h3>
      <table>
        <tbody>
          <tr><td><code>CHAIN_ID</code></td><td><code>0xc01dfeed</code>: baked into every tx signature; cross-chain replay is rejected at signature-verify time.</td></tr>
          <tr><td>Smallest unit</td><td>1 BRC = 10⁸ wei</td></tr>
          <tr><td>Max supply</td><td>21,000,000 BRC</td></tr>
          <tr><td>Target block time</td><td>150 s (2.5 min)</td></tr>
          <tr><td>Max block size</td><td>256 KB</td></tr>
          <tr><td>Min fee</td><td>1 wei per byte (≥ 152 wei per tx)</td></tr>
          <tr><td>Default helper port</td><td><code>9000</code> (HTTP API), <code>9001</code> (PeerJS signaling)</td></tr>
        </tbody>
      </table>

      <h3>REST API</h3>
      <p>
        Base URL is whichever helper server you point at. Run your own with
        <code>npm run server:api</code> or add a URL under
        <strong>Settings → Helper servers</strong>. All integers on the wire
        are big-endian.
      </p>
      <table>
        <thead><tr><th>Method</th><th>Path</th><th>Purpose</th></tr></thead>
        <tbody>
          <tr><td>GET</td><td><code>/tip</code></td><td>Latest height + tip hash</td></tr>
          <tr><td>GET</td><td><code>/blocks?fromHeight=&amp;max=</code></td><td>Canonical blocks oldest-first (max 200)</td></tr>
          <tr><td>GET</td><td><code>/stats</code></td><td>Peer count, miners active, server height, etc.</td></tr>
          <tr><td>GET</td><td><code>/peers</code></td><td>Active peer IDs (for direct WebRTC dial)</td></tr>
          <tr><td>GET</td><td><code>/mempool</code></td><td>Pending tx hex list</td></tr>
          <tr><td>POST</td><td><code>/block</code></td><td>Submit a mined block</td></tr>
          <tr><td>POST</td><td><code>/txs</code></td><td>Submit transactions</td></tr>
          <tr><td>POST</td><td><code>/heartbeat</code></td><td>Browser keepalive (clients only)</td></tr>
        </tbody>
      </table>

      <p>The quickest possible smoke test:</p>
      <pre><code>curl http://localhost:9000/tip
# { "height": 12345, "tipHash": "a1b2…" }</code></pre>

      <h3>Transaction wire format</h3>
      <p>
        152 bytes total, big-endian. The first 88 bytes are the signed preimage;
        the trailing 64 bytes are an Ed25519 signature over them.
      </p>
      <table>
        <thead><tr><th>Offset</th><th>Length</th><th>Field</th></tr></thead>
        <tbody>
          <tr><td>0</td><td>4</td><td><code>chainId</code>: always <code>0xc01dfeed</code></td></tr>
          <tr><td>4</td><td>32</td><td><code>from</code>: Ed25519 pubkey. <strong>This is the address</strong>: no hashing, no checksum.</td></tr>
          <tr><td>36</td><td>32</td><td><code>to</code>: Ed25519 pubkey of recipient</td></tr>
          <tr><td>68</td><td>8</td><td><code>amount</code> (u64 wei)</td></tr>
          <tr><td>76</td><td>8</td><td><code>fee</code> (u64 wei)</td></tr>
          <tr><td>84</td><td>4</td><td><code>nonce</code> (u32, per-sender, must equal <code>state.nonce</code> .. <code>state.nonce+16</code>)</td></tr>
          <tr><td><strong>88</strong></td><td>64</td><td><code>signature</code>: Ed25519 (RFC 8032) over bytes <code>[0, 88)</code></td></tr>
        </tbody>
      </table>
      <p>
        Transaction ID = <code>sha256</code> of the full 152-byte encoding
        (signature included).
      </p>

      <h3>Block wire format</h3>
      <p>
        148-byte header + 4-byte tx count + N × 152-byte transactions.
        Block hash = <code>sha256</code> of header bytes (body excluded).
      </p>
      <table>
        <thead><tr><th>Offset</th><th>Length</th><th>Field</th></tr></thead>
        <tbody>
          <tr><td>0</td><td>4</td><td><code>height</code> (u32; genesis is 0)</td></tr>
          <tr><td>4</td><td>32</td><td><code>prevHash</code>: sha256 of parent header</td></tr>
          <tr><td>36</td><td>32</td><td><code>txRoot</code>: Merkle root over <code>encodeTx(tx)</code></td></tr>
          <tr><td>68</td><td>32</td><td><code>stateRoot</code>: account-tree root after applying this block</td></tr>
          <tr><td>100</td><td>8</td><td><code>timestamp</code> (u64 unix seconds)</td></tr>
          <tr><td>108</td><td>4</td><td><code>difficulty</code>: compact target (Bitcoin-style "bits")</td></tr>
          <tr><td>112</td><td>4</td><td><code>nonce</code> (u32)</td></tr>
          <tr><td>116</td><td>32</td><td><code>miner</code>: pubkey credited reward + fees</td></tr>
        </tbody>
      </table>

      <h3>Proof-of-Work</h3>
      <p>
        A header is valid when its PoW hash, interpreted as a big-endian
        uint256, is less than the target decoded from the header's compact
        <code>difficulty</code> field. Which hash function applies is gated on
        the header's own <code>height</code> (its first 4 bytes, u32be), so a
        verifier needs nothing but the header to pick the right one:
      </p>
      <table>
        <thead><tr><th>Height</th><th>Algorithm</th></tr></thead>
        <tbody>
          <tr><td>0 – 33,549</td><td>Argon2id: legacy, still required to re-verify history</td></tr>
          <tr><td>33,550 and above</td><td><strong>Sandglass v3</strong>: current (fork #2)</td></tr>
        </tbody>
      </table>

      <h4>Sandglass v3 (current)</h4>
      <p>
        Memory-<em>latency</em>-hard: a 512 KiB buffer walked by a long chain of
        dependent random reads. Uses only <code>Math.imul</code>-equivalent
        32-bit multiplies, uint32 arithmetic, and SHA-256, so it is bit-exact
        across languages and engines. All arithmetic is mod 2³², all buffer
        indices are <code>&amp; 0x1FFFF</code>.
      </p>
      <pre><code>mix(x):                        // lowbias32
  x ^= x >>> 16;  x = (x * 0x7feb352d) mod 2^32
  x ^= x >>> 15;  x = (x * 0x846ca68b) mod 2^32
  x ^= x >>> 16;  return x

W = 131072 (u32 words = 512 KiB)   GOLDEN = 0x9e3779b9
seed = sha256(header)              // 32 bytes
sw[0..7] = seed as 8 × u32be

// 1. fill: chained, one seed word injected per step
h = mix(sw[0] ^ GOLDEN)
for i in 0..W-1:
    h = mix(h + GOLDEN + sw[i & 7]);  buf[i] = h

// 2. init 4 chains from h
x = h
for c in 0..3:
    x  = mix(x ^ (c+1))
    a[c] = mix(x ^ GOLDEN)
    i[c] = x & (W-1)

// 3. walk: 4 interleaved dependent read-modify-writes, 524288 steps each
for s in 0..524287:
    for c in 0..3:
        a[c] = mix(a[c] ^ buf[i[c]])
        buf[i[c]] = (a[c] + s) mod 2^32
        i[c] = a[c] & (W-1)

// 4. finalize
fin = seed ‖ u32be(h) ‖ u32be(a0) ‖ u32be(a1) ‖ u32be(a2) ‖ u32be(a3)   // 52 bytes
powHash = sha256(fin)</code></pre>
      <p>
        Note the chains are stepped in lockstep (all four advance one step per
        iteration <code>s</code>, and <code>s</code> is mixed into every store):
        running one chain to completion before the next produces a different
        digest. Frozen test vectors live at
        <a href="https://github.com/swompythesecond/BrowserCoin/blob/main/src/crypto/sandglass.vectors.json" target="_blank" rel="noopener noreferrer">src/crypto/sandglass.vectors.json</a>;
        any independent implementation must reproduce them exactly.
      </p>

      <h4>Argon2id (legacy, height &lt; 33,550)</h4>
      <p>
        Required only to re-verify pre-fork history. Password is the 148-byte
        header; output is compared against the target the same way.
      </p>
      <table>
        <tbody>
          <tr><td>Algorithm</td><td>Argon2id (RFC 9106)</td></tr>
          <tr><td>Memory</td><td>32 MiB</td></tr>
          <tr><td>Iterations</td><td>1</td></tr>
          <tr><td>Parallelism</td><td>1</td></tr>
          <tr><td>Output length</td><td>32 bytes</td></tr>
          <tr><td>Salt</td><td>UTF-8 of the literal string <code>browsercoin-pow-v5</code></td></tr>
        </tbody>
      </table>

      <h3>Signing a transaction</h3>
      <p>
        Ed25519 (RFC 8032) over the 88-byte preimage. Any conformant library
        works; the in-browser node uses <code>@noble/ed25519</code>.
      </p>
      <pre><code>import * as ed from '@noble/ed25519';

// 88 bytes: chainId(4) ‖ from(32) ‖ to(32) ‖ amount(8) ‖ fee(8) ‖ nonce(4)
const preimage = txPreimage({ from, to, amount, fee, nonce });
const signature = await ed.signAsync(preimage, privKey);  // 64 bytes
const txBytes = concat(preimage, signature);              // 152 bytes total
const txHex = toHex(txBytes);

await fetch(API_BASE + '/txs', {
  method: 'POST',
  headers: { 'content-type': 'application/json' },
  body: JSON.stringify({ txs: [txHex] }),
});</code></pre>

      <p>
        Complete runnable script (key generation, tip fetch, signing, submit):
        <a href="https://github.com/swompythesecond/BrowserCoin/blob/main/examples/send-tx.mjs" target="_blank" rel="noopener noreferrer">examples/send-tx.mjs</a>.
      </p>

      <h3>Run your own helper</h3>
      <p>
        The helpers are pluggable and plural. None of them is an authority:
        every block they accept is validated locally exactly like a
        peer-relayed block, and browsers verify everything themselves anyway.
        A malicious helper can withhold blocks but cannot trick clients into
        accepting invalid ones.
      </p>
      <pre><code>git clone https://github.com/swompythesecond/BrowserCoin
cd BrowserCoin
npm install
npm run server:api      # → :9000
npm run server:peerjs   # → :9001 (independent process)</code></pre>
      <p>
        Add your URL under <strong>Settings → Helper servers</strong>. The
        browser app fans out reads/writes across every configured helper, so
        more helpers = a more resilient network.
      </p>

      <h3>Stability</h3>
      <p>
        This is v0.2. Expect breakage: wire format, endpoint shapes, and
        consensus tweaks may change. There is no API versioning header. The
        <code>CHAIN_ID</code> is the only fork-resistant identifier, and the
        PoW salt's <code>-v2</code> suffix exists so a future hard fork can
        bump to <code>-v3</code> and cleanly invalidate the old chain.
      </p>

      <h3>The code</h3>
      <p>
        MIT-licensed. Read it, run it, fork it:
        <a href="https://github.com/swompythesecond/BrowserCoin" target="_blank" rel="noopener noreferrer">github.com/swompythesecond/BrowserCoin</a>.
      </p>
    </article>
  `;
  host.appendChild(view);
  return () => { };
}
